import type { FinancePartnerRow, FinancingProduct } from '../../../../store/oneButtonStore';
import { selectedQuote } from './FinancingGridCard';

/** Keys used in the financing grid and `selectedFinanceCells` (partner::product). */
export interface QuoteCellContext {
    partner: string;
    product: string;
    methord?: string | null;
    price?: number | null;
    term?: number;
    apr?: number;
}

export type QuoteFormTabKind = 'solar' | 'roof' | 'battery';

export function columnIncludesSolar(col: string | undefined): boolean {
    return !!col && /\bSolar\b/i.test(col);
}

export function columnIncludesRoof(col: string | undefined): boolean {
    return !!col && /\bRoof\b/i.test(col);
}

export function columnIncludesBattery(col: string | undefined): boolean {
    return !!col && /\bBattery\b/i.test(col);
}

/**
 * Maps a financing grid column label to SVA Financing Options C4 notation (S, B, R, SR, SB, BR, SBR).
 * Aligns with Trinity generate-quote product type codes.
 */
export function svaProductCodeFromColumn(col: string | undefined): string {
    const s = columnIncludesSolar(col);
    const r = columnIncludesRoof(col);
    const b = columnIncludesBattery(col);
    if (s && r && b) return 'SBR';
    if (s && b) return 'SB';
    if (s && r) return 'SR';
    if (b && r) return 'BR';
    if (s) return 'S';
    if (b) return 'B';
    if (r) return 'R';
    return 'S';
}

/**
 * Roofing Calculator C4: strip first "B" from combo code (Trinity `productType.replace('B','')`), then handle partner-specific overrides.
 * Aligns with logic in roof-detail-form.component.ts and generate-quote.component.ts.
 */
export function roofCalculatorC4FromColumn(col: string | undefined, partner: string): string {
    let pt = svaProductCodeFromColumn(col).replace('B', '');
    const p = (partner || '').trim();

    // GoodLeap special case: SR -> R (Case-insensitive & handles space)
    const isGoodLeap = p === 'GoodLeap' || p === 'Goodleep' || p === 'Good Leap';
    if (isGoodLeap && pt === 'SR') return 'R';

    return pt || 'R';
}

/** SVA C4 for battery flows (GoodLeap BR→B; SB/SBR + method adjustments). */
export function batterySvaC4(partner: string, column: string | undefined, financeMethod: string | undefined): string {
    const p = (partner || '').trim();
    let pt = svaProductCodeFromColumn(column);
    if (p === 'GoodLeap' || p === 'Goodleep') {
        if (pt === 'BR') pt = 'B';
        const fm = (financeMethod || '').trim();
        if ((pt === 'SB' || pt === 'SBR') && fm === 'Battery Loan') pt = 'B';
        else if ((pt === 'SB' || pt === 'SBR') && fm.includes('Storage')) pt = 'SB';
    }
    return pt;
}

/** Sunnova Battery IO C4: BR is sent as B (Trinity). */
export function batteryCalculatorC4FromColumn(col: string | undefined): string {
    const pt = svaProductCodeFromColumn(col);
    return pt === 'BR' ? 'B' : pt;
}

function parseCellKey(key: string): { partner: string; product: FinancingProduct } | null {
    const idx = key.indexOf('::');
    if (idx < 0) return null;
    const partner = key.slice(0, idx);
    const product = key.slice(idx + 2) as FinancingProduct;
    return { partner, product };
}

function resolveQuoteFromGrid(
    partner: string,
    product: FinancingProduct,
    grid: FinancePartnerRow[],
    selectedQuote: selectedQuote | null
): selectedQuote | null {
    if (partner === 'Good Leap') partner = 'GoodLeap';
    const row = grid.find((r) => r.partner === partner);
    const cell = row?.prices[product];
    if (!row || !cell) return null;
    return {
        partner,
        product,
        methord: cell.financeMethod ?? '',
        price: cell.purchasePrice ?? null,
        ExcelOffer: selectedQuote?.ExcelOffer,
        payload: selectedQuote?.payload,
        term: cell.term,
        apr: cell.apr,
    };
}

function quotesFromKeys(keys: Iterable<string>, grid: FinancePartnerRow[], selectedQuote: any | null): selectedQuote[] {
    const out: selectedQuote[] = [];
    selectedQuote?.products?.forEach((ele: any) => {
        out.push(ele);
    });
    for (const key of keys) {
        const parsed = parseCellKey(key);
        if (!parsed) continue;
        const q = resolveQuoteFromGrid(parsed.partner, parsed.product, grid, selectedQuote);
        if (q) out.push(q);
    }
    const seen = new Set<string>();
    const dedup: selectedQuote[] = [];
    for (const q of out) {
        const sig = `${q.partner ?? ''}::${q.product ?? ''}`;
        if (seen.has(sig)) continue;
        seen.add(sig);
        dedup.push(q);
    }
    return dedup;
}

function pickQuoteForKind(
    kind: QuoteFormTabKind,
    quotes: selectedQuote[],
    clicked: selectedQuote | null,
): selectedQuote | null {
    const match = (q: selectedQuote) => {
        const c = q.product || '';
        if (kind === 'solar') return columnIncludesSolar(c);
        if (kind === 'roof') return columnIncludesRoof(c);
        return columnIncludesBattery(c);
    };
    const filtered = quotes.filter(match);
    if (filtered.length === 0) {
        // Fallback to clicked quote if it matches this kind, even if not in grid quotes
        if (clicked && match(clicked)) return clicked;
        return null;
    }
    if (clicked && match(clicked)) {
        let hit:any;
        if(clicked?.products?.length){
            const selected = clicked?.products.find( ele => ele.product.toLowerCase() === kind)
            if(selected) hit = filtered.find((q) => q.partner === selected.partner && q.product === selected.product);
            else hit = filtered.find((q) => q.partner === clicked.partner && q.product === clicked.product);
        } else hit = filtered.find((q) => q.partner === clicked.partner && q.product === clicked.product);
        if (hit) return hit;
    }
    return filtered[0];
}

export interface QuoteFormTab {
    key: string;
    kind: QuoteFormTabKind;
    label: string;
    quote: selectedQuote;
}

/**
 * Builds one tab per product kind (solar / roof / battery) from selected grid keys plus the cell that opened the modal.
 */
export function buildQuoteFormTabs(
    clicked: selectedQuote | null,
    selectedKeys: Set<string>,
    grid: FinancePartnerRow[],
): QuoteFormTab[] {
    const keys = new Set(selectedKeys);
    if (clicked) keys.add(`${clicked.partner}::${clicked.product}`);
    const quotes = quotesFromKeys(keys, grid, clicked);

    let kinds: QuoteFormTabKind[] = ['solar', 'roof', 'battery'];

    // If the clicked cell is specifically single-product related, we restrict tabs to that specific product kind
    if (clicked) {
        const isSolar = columnIncludesSolar(clicked.product);
        const isRoof = columnIncludesRoof(clicked.product);
        const isBattery = columnIncludesBattery(clicked.product);

        const activeKinds: QuoteFormTabKind[] = [];
        if (isSolar) activeKinds.push('solar');
        if (isRoof) activeKinds.push('roof');
        if (isBattery) activeKinds.push('battery');

        if (activeKinds.length > 0) {
            kinds = activeKinds;
        }
    }

    const tabs: QuoteFormTab[] = [];
    for (const kind of kinds) {
        const quote = pickQuoteForKind(kind, quotes, clicked);
        if (!quote) continue;
        const label =
            kind === 'solar' ? 'Solar' : kind === 'roof' ? 'Roof' : 'Battery';
        tabs.push({
            key: kind,
            kind,
            label,
            quote,
        });
    }
    if (tabs.length === 0 && clicked) {
        const kind = defaultQuoteTabKindForClick(clicked);
        const label = kind === 'solar' ? 'Solar' : kind === 'roof' ? 'Roof' : 'Battery';
        tabs.push({ key: kind, kind, label, quote: clicked });
    }
    return tabs;
}

export function defaultQuoteTabKindForClick(clicked: selectedQuote | null): QuoteFormTabKind {
    if (!clicked) return 'solar';
    const c = clicked.product || '';
    if (columnIncludesSolar(c)) return 'solar';
    if (columnIncludesBattery(c)) return 'battery';
    if (columnIncludesRoof(c)) return 'roof';
    return 'solar';
}
